import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { AudioVariantSummary, CatalogSong } from "@vocab1600/shared";
import { api } from "./api.js";

/**
 * 全局播放器。
 *
 * 这里刻意做成整个应用共享的单例：此前 usePlayer() 在歌单页、歌曲页、校准页
 * 各调用一次，每次都会 new Audio()，导致换页时旧实例仍在播放、新实例又起一个，
 * 出现多首歌同时播放，并且离开页面就失去控制。音频元素必须只有一个，
 * 状态挂在 Provider 上，任何页面通过 useAudioPlayer() 访问同一个实例。
 */

export interface PlayItem {
  songNumber: string;
  songTitle: string;
  unitName: string;
  hue: number;
  unitIcon: string;
  variant: AudioVariantSummary;
}

export interface PlayerState {
  current: PlayItem | null;
  playing: boolean;
  positionMs: number;
  durationMs: number;
  volume: number;
  loop: boolean;
  error: string | null;
  loading: boolean;
}

interface PlayerApi {
  state: PlayerState;
  play: (item: PlayItem) => void;
  toggle: (item?: PlayItem) => void;
  pause: () => void;
  seek: (positionMs: number) => void;
  setVolume: (value: number) => void;
  toggleLoop: () => void;
  next: () => void;
  previous: () => void;
  isCurrent: (variantKey: string) => boolean;
}

const PlayerContext = createContext<PlayerApi | null>(null);

const HEARTBEAT_MS = 30_000;

interface PendingEvent {
  eventType: string;
  occurredAt: string;
  songNumber?: string;
  variantKey?: string;
  durationMs?: number;
  positionMs?: number;
}

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<PendingEvent[]>([]);
  const flushTimerRef = useRef<number | null>(null);
  const currentRef = useRef<PlayItem | null>(null);

  const [state, setState] = useState<PlayerState>({
    current: null,
    playing: false,
    positionMs: 0,
    durationMs: 0,
    volume: 1,
    loop: false,
    error: null,
    loading: false
  });

  if (audioRef.current === null && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
  }

  /* ---------------------------------------------------------- 学习事件上报 */

  const enqueue = useCallback((event: PendingEvent) => {
    queueRef.current.push(event);
    if (flushTimerRef.current !== null) return;
    flushTimerRef.current = window.setTimeout(() => {
      const batch = queueRef.current.splice(0, queueRef.current.length);
      flushTimerRef.current = null;
      if (batch.length > 0) void api.sendEvents(batch);
    }, 2000);
  }, []);

  const track = useCallback((eventType: string, extra: Partial<PendingEvent> = {}) => {
    const item = currentRef.current;
    if (!item) return;
    enqueue({
      eventType,
      occurredAt: new Date().toISOString(),
      songNumber: item.songNumber,
      variantKey: item.variant.variantKey,
      ...extra
    });
  }, [enqueue]);

  /* -------------------------------------------------------------- 事件绑定 */

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTime = () => setState((s) => ({ ...s, positionMs: Math.floor(el.currentTime * 1000) }));
    const onMeta = () => setState((s) => ({
      ...s,
      durationMs: Number.isFinite(el.duration) ? Math.floor(el.duration * 1000) : s.durationMs,
      loading: false
    }));
    const onPlay = () => setState((s) => ({ ...s, playing: true, error: null, loading: false }));
    const onWaiting = () => setState((s) => ({ ...s, loading: true }));
    const onPause = () => {
      track("play_paused", { positionMs: Math.floor(el.currentTime * 1000) });
      setState((s) => ({ ...s, playing: false }));
    };
    const onEnded = () => {
      track("play_completed", { positionMs: Math.floor(el.currentTime * 1000) });
      setState((s) => ({ ...s, playing: false, positionMs: 0 }));
    };
    const onError = () => {
      setState((s) => ({ ...s, playing: false, loading: false, error: "音频加载失败，正在诊断…" }));
      const url = el.src;
      if (!url) return;
      // HTMLAudioElement 的 error 事件不带 HTTP 状态码，补一次 1 字节探测问出真实原因
      void fetch(url, { headers: { Range: "bytes=0-0" }, credentials: "same-origin", cache: "no-store" })
        .then((res) => {
          const message =
            res.status === 401 ? "登录状态已失效，请重新登录后再播放"
            : res.status === 403 ? "服务器拒绝读取该音频文件，请联系管理员检查文件权限"
            : res.status === 404 ? "该旋律版本的音频尚未上传到服务器"
            : res.status === 409 ? "该旋律版本未通过音质检查，暂不可播放"
            : res.ok ? "音频可访问但浏览器解码失败，文件可能损坏"
            : `播放失败（服务器返回 ${res.status}）`;
          setState((s) => ({ ...s, error: message }));
        })
        .catch(() => setState((s) => ({ ...s, error: "网络异常，无法连接到音频服务" })));
    };

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
    };
  }, [track]);

  // 播放中每 30 秒上报一次心跳，作为"有效收听时长"的唯一来源
  useEffect(() => {
    if (!state.playing) return;
    const timer = window.setInterval(() => {
      const el = audioRef.current;
      track("play_heartbeat", {
        durationMs: HEARTBEAT_MS,
        ...(el ? { positionMs: Math.floor(el.currentTime * 1000) } : {})
      });
    }, HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [state.playing, track]);

  // 关闭或切走页面时把队列里剩余事件送出，否则最后一段收听会丢失
  useEffect(() => {
    const flush = () => {
      const batch = queueRef.current.splice(0, queueRef.current.length);
      if (batch.length > 0) void api.sendEvents(batch);
    };
    const onHidden = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHidden);
      flush();
    };
  }, []);

  /* ------------------------------------------------------------ 控制方法 */

  const play = useCallback((item: PlayItem) => {
    const el = audioRef.current;
    if (!el) return;
    const isSame = currentRef.current?.variant.variantKey === item.variant.variantKey;
    if (!isSame) {
      currentRef.current = item;
      el.src = item.variant.streamUrl;
      setState((s) => ({
        ...s,
        current: item,
        positionMs: 0,
        durationMs: item.variant.durationMs,
        error: null,
        loading: true
      }));
    }
    void el.play()
      .then(() => track("play_started", { positionMs: Math.floor(el.currentTime * 1000) }))
      .catch((err: unknown) => {
        const name = err instanceof Error ? err.name : "";
        if (name === "NotAllowedError") {
          setState((s) => ({ ...s, error: "浏览器阻止了自动播放，请再点一次播放按钮", loading: false }));
        } else if (name !== "AbortError") {
          setState((s) => ({ ...s, error: "播放启动失败，正在诊断…", loading: false }));
        }
      });
  }, [track]);

  const pause = useCallback(() => audioRef.current?.pause(), []);

  const toggle = useCallback((item?: PlayItem) => {
    const el = audioRef.current;
    if (!el) return;
    const target = item ?? currentRef.current;
    if (!target) return;
    const isSame = currentRef.current?.variant.variantKey === target.variant.variantKey;
    if (isSame && !el.paused) pause();
    else play(target);
  }, [pause, play]);

  const seek = useCallback((positionMs: number) => {
    const el = audioRef.current;
    if (!el) return;
    const target = Math.max(0, positionMs) / 1000;
    if (Number.isFinite(el.duration)) el.currentTime = Math.min(target, el.duration);
    else el.currentTime = target;
    setState((s) => ({ ...s, positionMs: Math.floor(el.currentTime * 1000) }));
  }, []);

  const setVolume = useCallback((value: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = Math.min(Math.max(value, 0), 1);
    setState((s) => ({ ...s, volume: el.volume }));
  }, []);

  const toggleLoop = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.loop = !el.loop;
    setState((s) => ({ ...s, loop: el.loop }));
  }, []);

  /** 切歌：在完整歌单里前后移动，取目标歌曲的第一个可用版本 */
  const step = useCallback(async (delta: number) => {
    const item = currentRef.current;
    const songs = window.APP_SONGS ?? [];
    if (!item || songs.length === 0) return;
    const index = songs.findIndex((song) => song.num === item.songNumber);
    if (index < 0) return;
    for (let offset = 1; offset <= songs.length; offset += 1) {
      const nextIndex = (index + delta * offset + songs.length * offset) % songs.length;
      const song = songs[nextIndex];
      if (!song) continue;
      try {
        const { variants } = await api.songVariants(song.num);
        const variant = variants[0];
        if (!variant) continue;
        play({
          songNumber: song.num,
          songTitle: song.title,
          unitName: song.unitName,
          hue: song.hue,
          unitIcon: song.unitIcon,
          variant
        });
        return;
      } catch { /* 该首没有可用版本，继续找下一首 */ }
    }
  }, [play]);

  const next = useCallback(() => { void step(1); }, [step]);
  const previous = useCallback(() => { void step(-1); }, [step]);

  const isCurrent = useCallback(
    (variantKey: string) => currentRef.current?.variant.variantKey === variantKey,
    []
  );

  const value = useMemo<PlayerApi>(
    () => ({ state, play, toggle, pause, seek, setVolume, toggleLoop, next, previous, isCurrent }),
    [state, play, toggle, pause, seek, setVolume, toggleLoop, next, previous, isCurrent]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function useAudioPlayer(): PlayerApi {
  const context = useContext(PlayerContext);
  if (!context) throw new Error("useAudioPlayer 必须在 PlayerProvider 内使用");
  return context;
}

/** 把目录里的歌曲与变体组装成播放项 */
export function toPlayItem(song: CatalogSong, variant: AudioVariantSummary): PlayItem {
  return {
    songNumber: song.num,
    songTitle: song.title,
    unitName: song.unitName,
    hue: song.hue,
    unitIcon: song.unitIcon,
    variant
  };
}

export function formatTime(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0:00";
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
